import { ApolloError } from '@apollo/client';
import { PropertyMap, UserAction } from '@thoughtspot/metrics';
import { ToastAlertProps } from '@thoughtspot/radiant-react/widgets/alert';
import { ButtonType } from '@thoughtspot/radiant-react/widgets/button/button';
import { IconID } from '@thoughtspot/radiant-react/widgets/icon/icon-constants';
import { InviteModalType } from '@thoughtspot/session-service';
import { InvitedUsersInfo } from '../setup-hub/setup-hub-interface';

export interface InvitedUser {
    address: string;
    errorMessage?: string;
}

export interface InviteUsersModalProps {
    isModalOpen: boolean;
    onModalClose: () => void;
    onModalConfirm: (
        invitedUsers: string[],
        description: string,
    ) => Promise<InvitedUsersInfo>;
    onEmailAdd: (user: string) => Promise<boolean>;
    inviteModalType: InviteModalType;
    supportEmail: string;
    collectEvent: (userAction: UserAction, property: PropertyMap) => void;
    errorMsg?: string;
    setIsToastVisible: (isToastVisible: boolean) => void;
    isCopilotEnabled?: boolean;
    source: InviteSource;
}

export interface InviteUsersContentProps {
    invitedUsers: InvitedUser[];
    onEmailAdd: (user: string) => void;
    onEmailDelete: (user: string) => void;
    onDescriptionAdd: (description: string) => void;
    inputEmail: string;
    setInputEmail: (text: string) => void;
    inputEmailHasError: boolean;
    setInputEmailHasError: (state: boolean) => void;
    isCopilotEnabled?: boolean;
}

export interface InviteUsersProps {
    // external function that validates email.
    onEmailAdd: (user: string) => Promise<boolean>;
    onInviteComplete?: any;
}

export interface InviteUsersContainerProps extends InviteUsersProps {
    showModals: boolean;
    onModalCloseCB: () => void;
    isCopilotEnabled?: boolean;
    source: InviteSource;
    // Seats left on the org's Essentials plan; no cap when undefined.
    maxInvites?: number;
}

export interface InviteUsersButtonProps extends InviteUsersProps {
    // button text for example Invite Others/ Invite teammates.
    buttonText?: string;
    // Invite button icon.
    buttonIcon?: IconID;
    // if true then button property isFullWidth is set to true.
    isFullWidth?: boolean;
    // button type.
    buttonType?: ButtonType;
    // function to be executed when Invite button is clicked,
    // if true modal is opened on button click.
    buttonClickFn?: () => boolean;
    shouldCollectInviteBtnEvent?: boolean;
    onInviteBtnClickUserAction?: UserAction;
    updateToastAlert: (alertProps: ToastAlertProps) => void;
    isInviteLinkCall?: boolean;
    source: InviteSource;
}

export interface InviteCopilotUsersButtonProps
    extends Omit<InviteUsersButtonProps, 'buttonIcon'> {
    buttonIcon: string;
    source: InviteSource;
}

export interface InviteLinkProps {
    linkClassname?: string;
    linkTextClassname?: string;
    linkText?: string;
}

export interface UpdateDisplayNameContainerProps {
    isUpdateDisplayNameModalOpen: boolean;
    setIsUpdateDisplayNameModalOpen: (isModalOpen: boolean) => void;
    onUpdateCB: () => void;
    onDismissCB: () => void;
    origin: UpdateDisplayNameDialogOrigin;
}

export interface UpdateDisplayNameProps {
    updateDisplayName: (displayName: string) => void;
    updateDisplayNameError: ApolloError;
    isModalOpen: boolean;
    isUpdateInProgress: boolean;
    onDismissCB: () => void;
    originalDisplayName: string;
    origin: UpdateDisplayNameDialogOrigin;
}

export enum UpdateDisplayNameDialogOrigin {
    INVITE,
    SHARE,
}

export enum InviteSource {
    FT_BANNER = 'FT_BANNER',
    SETUP_HUB = 'SETUP_HUB',
    TEAMS_LEFT_PANEL = 'TEAMS_LEFT_PANEL',
    USER_PROFILE = 'USER_PROFILE',
}
